import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-types-papi';
import { default as SharedFeedThumb } from '@wix/da-shared-react/pkg/DeviationViews/Feed';
import { NullableDropdownItem } from '@wix/da-shared-react/pkg/Dropdown/types';
import PinIcon from '@wix/da-shared-react/pkg/Icons/24x24/Pin';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import s from './FeedThumb.scss';

export interface Props {
  className?: string;
  deviation: PapiDeviation;

  // Privileges
  canPin: boolean;
  canEdit: boolean;

  // Actinos
  editItem: () => void;
  pinItem: () => void;
  unpinItem: () => void;

  // SharedFeedThumbProps
  withComments?: boolean;
  withWatch?: boolean;

  extraActions?: NullableDropdownItem[];
}

const FeedThumb: React.FC<Props> = ({
  deviation,
  deviation: { isPinned },
  className,
  canPin,
  canEdit,
  editItem,
  pinItem,
  unpinItem,
  withComments,
  withWatch,
  extraActions = [],
}) => {
  const { t } = useTranslation();
  // TODO: consolidate with:
  // - Sections/PostsSection__profile_posts_feed/_partials/PostsFeed/PostsFeed.tsx
  // - Sections/PostsSection/Journals/JournalsList/JournalFeedThumb/JournalFeedThumb.tsx
  const pinAndEditActions: NullableDropdownItem[] = useMemo(
    () => [
      canPin && {
        label: isPinned ? t('widgets.feed.unpin') : t('widgets.feed.pin'),
        onClick: isPinned ? unpinItem : pinItem,
        biData: {
          evid: BiEventType.WATCH_MENU_ITEM_SELECTION,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
          menu_item: isPinned ? 'unpin_to_top' : 'pin_to_top',
        },
      },
      canEdit && {
        label: t('widgets.feed.edit'),
        onClick: editItem,
        biData: {
          evid: BiEventType.WATCH_MENU_ITEM_SELECTION,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
          menu_item: 'edit',
        },
      },
    ],
    [t, canPin, isPinned, deviation, pinItem, unpinItem, canEdit, editItem]
  );

  return (
    <div className={classnames(s['root'], className)}>
      {isPinned && <PinIcon withPadding className={s['pinned-icon']} />}
      <SharedFeedThumb
        deviation={deviation}
        withComments={withComments}
        withWatch={withWatch}
        extraActions={[...pinAndEditActions, ...extraActions]}
      />
    </div>
  );
};
FeedThumb.displayName = 'FeedThumb';

export default FeedThumb;
